import React from 'react';

/**
 * SegmentedControl — Tab-style toggle for switching between views.
 * Uses logical CSS properties for RTL support (ms-/me- instead of ml-/mr-).
 */

interface SegmentedControlProps {
  /** Active tab value */
  value: string;
  /** Available options */
  options: Array<{ label: string; value: string; icon?: React.ReactNode }>;
  /** Change handler */
  onChange: (_value: string) => void;
}

export function SegmentedControl({ value, options, onChange }: SegmentedControlProps) {
  return (
    <div className="flex w-full sm:w-auto rounded-md overflow-hidden" role="tablist" aria-label="View selection">
      {options.map((option, i) => (
        <button
          key={option.value}
          id={`segment-${option.value}`}
          role="tab"
          aria-selected={value === option.value}
          aria-controls={`panel-${option.value}`}
          onClick={() => onChange(option.value)}
          className={`
            flex flex-1 sm:flex-none items-center justify-center gap-2 px-3.5 py-2.5 font-body text-sm font-semibold
            border-0 cursor-pointer outline-none
            transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-green-400
            ${i > 0 ? 'border-s border-gray-200' : ''}
            ${value === option.value
              ? 'bg-green-700 text-white'
              : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
            }
          `}
        >
          {option.icon && <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">{option.icon}</span>}
          <span>{option.label}</span>
        </button>
      ))}
    </div>
  );
}

export type { SegmentedControlProps };
